import React, { useState } from "react";
import { Check, ShieldAlert, ExternalLink } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC } from "@/trpc";
import { HiringProcessAssigneeRole } from "@api/shared-runtime/enums";
import type { RequirementEligibility } from "@api/services/job-post/pure-requirements-evaluator";
import { HiringProcessChecklistDialog } from "@/features/hiring-process/hiring-process-checklist-dialog";
import { Link } from "@tanstack/react-router";
import {
  parseRequirementsBlockedError,
  type RequirementsBlockedCause,
} from "./utils/requirements-blocked";

type DialogPhase = "confirm" | "next-steps";

export const JobPostAcceptMatchDialog: React.FC<{
  jobId: string;
  eligibility?: RequirementEligibility;
  onClose: () => void;
  onSuccess: () => void;
  sourceUserId?: string | null;
}> = ({ jobId, eligibility, onClose, onSuccess, sourceUserId }) => {
  const trpc = useTRPC();

  const [phase, setPhase] = useState<DialogPhase>("confirm");
  const [requirementsBlocked, setRequirementsBlocked] =
    useState<RequirementsBlockedCause | null>(null);
  const [genericError, setGenericError] = useState(false);

  const localBlocker: RequirementsBlockedCause | null = eligibility?.blocked
    ? {
        code: "REQUIREMENTS_BLOCKED",
        failed: eligibility.failed,
      }
    : null;

  // Reactive blocker (from a server-side throw on setDecision) wins over
  // local — the server is authoritative if they disagree.
  const activeBlocker = requirementsBlocked ?? localBlocker;

  const { mutateAsync: setDecision, isPending } = useMutation(
    trpc.jobPost.setDecision.mutationOptions(),
  );

  const handleAccept = async (): Promise<void> => {
    setRequirementsBlocked(null);
    setGenericError(false);

    try {
      await setDecision({
        id: jobId,
        interested: true,
        decisionReasonIds: [],
        ...(sourceUserId ? { sourceUserId } : {}),
      });
    } catch (err) {
      const blocked = parseRequirementsBlockedError(err);
      if (blocked) {
        setRequirementsBlocked(blocked);
        return;
      }
      setGenericError(true);
      return;
    }

    // Always call onSuccess to invalidate parent queries
    onSuccess();

    // Fetch the newly-created instance for this job
    const instance = await queryClient.fetchQuery(
      trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId: jobId }),
    );

    const items =
      instance?.items.filter(
        (i) => i.assigneeRole === HiringProcessAssigneeRole.Provider,
      ) ?? [];

    if (items.length > 0) {
      setPhase("next-steps");
    } else {
      toast("Application Sent", {
        description:
          "Your profile has been shared with the facility. They'll review it and contact you if it's a match.",
        duration: 8000,
        icon: <Check className="h-5 w-5 text-green-500" />,
      });
      onClose();
    }
  };

  if (phase === "next-steps") {
    return (
      <HiringProcessChecklistDialog
        jobPostId={jobId}
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      />
    );
  }

  return (
    <Dialog open={true} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-lg sm:rounded-2xl">
        <DialogHeader>
          <div
            className={cn(
              "mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl p-4",
              activeBlocker ? "bg-amber-50" : "bg-primary/5",
            )}
          >
            {activeBlocker ? (
              <ShieldAlert className="h-6 w-6 text-amber-600" />
            ) : (
              <Check className="h-6 w-6 text-primary" />
            )}
          </div>
          <DialogTitle className="text-center text-2xl font-semibold tracking-tight text-slate-900">
            {activeBlocker ? "Requirements not met" : "Apply to this job?"}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed text-slate-600 sm:px-4">
            {activeBlocker
              ? "You don't yet qualify for this role — see below."
              : "Applying shares your profile with the facility for review. It doesn't guarantee an offer — the facility will contact you if it's a match."}
          </DialogDescription>
        </DialogHeader>

        {/* REQUIREMENTS_BLOCKED inline blocker (preemptive or reactive) */}
        {activeBlocker && (
          <Alert className="my-4 border-amber-200 bg-amber-50">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">
              You don&apos;t meet this role&apos;s requirements
            </AlertTitle>
            <AlertDescription className="text-amber-800 space-y-3">
              {activeBlocker.failed.length > 0 ? (
                <>
                  <p className="text-sm">
                    The following requirements must be met before you can apply:
                  </p>
                  <ul className="space-y-1.5">
                    {activeBlocker.failed.map((req) => (
                      <li
                        key={req.id}
                        className="flex flex-wrap items-start gap-2"
                      >
                        <Badge
                          variant="outline"
                          className="border-amber-300 bg-amber-100 text-amber-800 text-xs whitespace-normal text-left"
                        >
                          {req.displayLabel}
                        </Badge>
                        {req.deepLink && (
                          <Link
                            to={req.deepLink as never}
                            className="text-xs text-amber-700 underline inline-flex items-center gap-1 hover:text-amber-900"
                            onClick={onClose}
                          >
                            Add to credentials
                            <ExternalLink size={10} />
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm">
                  Your profile is missing credentials required for this role.
                </p>
              )}
              <div className="pt-1">
                <Link
                  to="/provider/profile"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 underline hover:text-amber-700"
                  onClick={onClose}
                >
                  Go to My Profile
                  <ExternalLink size={12} />
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Generic error */}
        {genericError && !activeBlocker && (
          <Alert variant="destructive" className="my-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              There was a problem submitting your interest. Please try again.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex flex-col sm:flex-row justify-center gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          {!activeBlocker && (
            <Button
              type="button"
              onClick={handleAccept}
              disabled={isPending}
              className="w-full sm:w-auto"
            >
              {isPending ? "Submitting..." : "Apply & Share Profile"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
